/**
 * Environment-aware asset fetching utilities.
 * Provides JSON fetching that works in browser, Node.js (lambda/tests), and handles caching.
 * @module
 */
import type fs from "fs";
import { assert, assertIsNotUndefined } from "./assertOfType";
import { isOnNodeJS } from "./context";
import { fetchWithTimeoutAndRetry } from "./fetchHelpers";
import { getNodePublicAssetPath } from "./publicAssetPaths";

export type AssetFetcherMode = "Browser" | "NodeLocal";

export interface AssetFetcherConfig {
  readonly mode: AssetFetcherMode;
  readonly cacheSuffix?: string;
}

/**
 * Get the current asset fetcher configuration.
 * Useful for other modules that need to read the current mode/endpoint/fs.
 */
export function getAssetFetcherConfig(): Readonly<AssetFetcherConfig> {
  return config;
}

/**
 * Auto-detect and setup the asset fetcher based on the environment.
 */
function initializeAssetFetcherConfig(): AssetFetcherConfig {
  if (isOnNodeJS()) {
    // Local Node.js (tests or Lambda) - use filesystem
    return { mode: "NodeLocal", cacheSuffix: process.env.GIT_COMMIT_HASH || "" };
  } else {
    // Browser environment
    return { mode: "Browser", cacheSuffix: process.env.PUBLIC_GIT_COMMIT_HASH || "" };
  }
}

const config = initializeAssetFetcherConfig();

let localFs: typeof fs | undefined = undefined;
async function ensureFsModuleIsImported(): Promise<typeof fs> {
  assert(config?.mode === "NodeLocal", "Asset fetcher mode must be NodeLocal to import the fs module!");
  if (localFs === undefined) {
    localFs = await import("fs");
  }
  return localFs;
}

export async function readAssetFromFilesystem(path: string, encoding?: undefined): Promise<Buffer<ArrayBuffer>>;
export async function readAssetFromFilesystem(path: string, encoding: BufferEncoding): Promise<string>;
export async function readAssetFromFilesystem(
  path: string,
  encoding?: BufferEncoding,
): Promise<string | Buffer<ArrayBuffer>> {
  await ensureFsModuleIsImported();
  assertIsNotUndefined(localFs, "Asset fetcher fs module not setup for NodeLocal mode!");
  return localFs.readFileSync(path, encoding);
}

/**
 * Add cache-busting suffix to a URL.
 */
export function addCacheSuffixToURL(url: string): string {
  if (url.startsWith("data:") || url.startsWith("blob:") || !config.cacheSuffix) {
    return url;
  }
  assert(
    url.search(/[?&]nocache=/) === -1,
    "URL should not contain an existing nocache parameter when trying to add it.",
  );
  if (url.includes("?")) {
    url += "&";
  } else {
    url += "?";
  }
  url += "nocache=" + config.cacheSuffix;
  return url;
}

/**
 * Process a URL for the current environment.
 */
export function processURL(url: string, forceRefresh: boolean = false): string {
  if (config.cacheSuffix || forceRefresh) {
    url = addCacheSuffixToURL(url);
  }

  return url;
}

/**
 * Fetch JSON content from a URL or filesystem.
 * Works in browser, lambda, and local Node.js environments.
 *
 * @param url - The URL to fetch (e.g., "/static/palettes/foo.json")
 * @param forceRefresh - Force cache refresh by adding cache-busting parameter
 * @returns The JSON content as a string, or undefined on error
 */
export async function fetchJsonAsset(url: string, forceRefresh: boolean = false): Promise<string | undefined> {
  if (config.mode === "Browser") {
    const processedUrl = processURL(url, forceRefresh);

    try {
      const res = await fetchWithTimeoutAndRetry(processedUrl, {
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      });

      return res.text();
    } catch (e) {
      console.error(e);
      return undefined;
    }
  } else {
    // NodeLocal mode - read from filesystem
    try {
      return readAssetFromFilesystem(getNodePublicAssetPath(url, __dirname), "utf8");
    } catch (e) {
      console.error("Failed to read asset from filesystem:", e);
      return undefined;
    }
  }
}
